/**
 * Constantes partagées entre l'API, le SDK et le site.
 */

/**
 * Bumper cette valeur invalide TOUT le cache (edge + R2) d'un seul coup,
 * puisqu'elle entre dans le calcul de la clé de cache.
 * À incrémenter à chaque changement visuel du rendu.
 */
export const RENDERER_VERSION = 1;

/**
 * Les quatre `article*` partagent la même mise en page du texte et ne
 * diffèrent que par la composition de leur colonne illustrée : géométrie
 * d'origine, vignette dégradée, motif dérivé du titre, monogramme. Toutes
 * acceptent le paramètre `image`, qui prend alors le pas sur la composition
 * générée.
 *
 * Cette liste est un contrat public : le SDK en dérive son type, la
 * documentation l'énumère, et un nom retiré casse les intégrations
 * existantes. On ajoute, on ne renomme pas.
 */
export const TEMPLATES = [
  'minimal',
  'gradient',
  'terminal',
  'article',
  'article-cover',
  'article-pattern',
  'article-mark',
] as const;
export type TemplateName = (typeof TEMPLATES)[number];

export const THEMES = ['dark', 'light'] as const;
export type ThemeName = (typeof THEMES)[number];

export const OUTPUT_FORMATS = ['png', 'jpeg', 'webp'] as const;
export type OutputFormat = (typeof OUTPUT_FORMATS)[number];

export const PLANS = ['free', 'pro', 'agency'] as const;
export type PlanName = (typeof PLANS)[number];

export interface PlanLimits {
  /**
   * Nombre d'images **générées** par mois — pas de requêtes servies.
   *
   * Une réponse servie depuis le cache edge ou R2 ne consomme rien : elle
   * n'a coûté ni CPU de rendu, ni écriture de stockage. Le nom de ce champ
   * est donc littéral, et c'est ce que la page tarifs promet. Voir
   * docs/adr/008-unite-facturee.md.
   *
   * Corollaire important pour quiconque modifie le comptage : seul
   * `UsageDelta.renders` doit alimenter ce compteur. Y remettre `requests`
   * ferait payer le cache au client, ce qui viderait de son sens l'argument
   * central du produit.
   */
  readonly monthlyImages: number;
  readonly requestsPerSecond: number;
  readonly customTemplates: number;
  readonly watermark: boolean;
  readonly analyticsRetentionDays: number;
}

export const PLAN_LIMITS: Record<PlanName, PlanLimits> = {
  free: {
    monthlyImages: 1_000,
    requestsPerSecond: 10,
    customTemplates: 0,
    watermark: true,
    analyticsRetentionDays: 7,
  },
  pro: {
    monthlyImages: 15_000,
    requestsPerSecond: 50,
    customTemplates: 3,
    watermark: false,
    analyticsRetentionDays: 90,
  },
  agency: {
    monthlyImages: 100_000,
    requestsPerSecond: 200,
    customTemplates: Number.POSITIVE_INFINITY,
    watermark: false,
    analyticsRetentionDays: 365,
  },
};

/** Dimensions OG canoniques. */
export const DEFAULT_WIDTH = 1200;
export const DEFAULT_HEIGHT = 630;

/** Durée du cache de validation d'une clé API. Fixe la fenêtre de révocation. */
export const API_KEY_CACHE_TTL_SECONDS = 60;

/** Budget CPU dur pour un rendu. Au-delà : RENDER_FAILED. */
export const RENDER_CPU_BUDGET_MS = 50;

export const API_KEY_PREFIXES = {
  public: 'myog_pk_',
  secret: 'myog_sk_',
} as const;
